import React, { useState, useEffect } from 'react';
import { 
  MessageOutlined, 
  UserOutlined,
  CalendarOutlined,
  ToolOutlined,
  PictureOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CheckOutlined,
  DownOutlined
} from '@ant-design/icons';
import { Switch, Select } from 'antd';

const personalizationTokens = [
  { id: 'first_name', label: 'First Name', icon: <UserOutlined /> },
  { id: 'last_name', label: 'Last Name', icon: <UserOutlined /> },
  { id: 'service_date', label: 'Service Date', icon: <CalendarOutlined /> },
  { id: 'service_type', label: 'Service Type', icon: <ToolOutlined /> },
];

const cooldownOptions = [
  { value: '7days', label: '7 days' },
  { value: '14days', label: '14 days' },
  { value: '30days', label: '30 days' },
  { value: '45days', label: '45 days' },
  { value: '60days', label: '60 days' },
  { value: '90days', label: '90 days' },
  { value: '120days', label: '120 days' },
  { value: '150days', label: '150 days' },
  { value: '180days', label: '180 days' },
  { value: '1year', label: '1 year' },
  { value: '18months', label: '18 months' },
  { value: '24months', label: '24 months' },
  { value: '36months', label: '36 months' },
  { value: '48months', label: '48 months' },
];

const SMSTemplateDelivery = ({ campaignData, onUpdate, onNext }) => {
  const [campaignName, setCampaignName] = useState(campaignData.campaignName || '');
  const [smsMessage, setSmsMessage] = useState(campaignData.smsMessage || '');
  const [cooldownPeriod, setCooldownPeriod] = useState(campaignData.cooldownPeriod || '18months');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(campaignData.quietHoursEnabled !== false);
  const [quietHoursFrom, setQuietHoursFrom] = useState(campaignData.quietHoursFrom || '9:00 PM');
  const [quietHoursTo, setQuietHoursTo] = useState(campaignData.quietHoursTo || '8:00 AM');

  const maxChars = 480;
  const charCount = smsMessage.length;
  const smsSegments = Math.ceil(charCount / 160) || 1;
  const charsUntilNextSegment = (smsSegments * 160) - charCount;

  useEffect(() => {
    onUpdate({
      campaignName,
      smsMessage,
      cooldownPeriod,
      quietHoursEnabled,
      quietHoursFrom,
      quietHoursTo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, smsMessage, cooldownPeriod, quietHoursEnabled, quietHoursFrom, quietHoursTo]);

  const insertToken = (tokenId) => {
    const token = `{{${tokenId}}}`;
    setSmsMessage(prev => prev + token);
  };

  const getMessagePreview = () => {
    let preview = smsMessage;
    preview = preview.replace(/\{\{first_name\}\}/g, 'John');
    preview = preview.replace(/\{\{last_name\}\}/g, 'Smith');
    preview = preview.replace(/\{\{service_date\}\}/g, 'Dec 15');
    preview = preview.replace(/\{\{service_type\}\}/g, 'HVAC Maintenance');
    return preview || 'Your message will appear here...';
  };

  const handleContinue = () => {
    onNext();
  };

  return (
    <div className="step-container sms-template-step">
      <div className="sms-header-icon">
        <MessageOutlined />
      </div>
      <h1 className="step-title">SMS Template & Delivery</h1>
      <p className="step-subtitle">Create your message and configure delivery settings</p>

      <div className="campaign-content">
        {/* GPS Triggered Delivery Banner */}
        <div className="gps-banner" style={{ marginBottom: '24px' }}>
          <div className="gps-banner-icon">
            <ClockCircleOutlined />
          </div>
          <div className="gps-banner-content">
            <strong>GPS-Triggered Delivery</strong>
            <span>Messages are sent automatically when your vehicles enter the notification range you selected. Quiet hours settings will override delivery if enabled.</span>
          </div>
        </div>

        {/* Row 1: Campaign Name and Attach Image side-by-side - Full Width */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px', marginRight: '100px' }}>
              
              {/* Campaign Name */}
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                transition: 'all 0.3s ease'
              }}>
                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Campaign Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Spring HVAC Maintenance Reminder"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid var(--paper-300)',
                    padding: '10px 12px',
                    width: '100%'
                  }}
                />
                <span className="form-hint" style={{ fontSize: '11px', color: 'var(--ink-200)', marginTop: '4px', display: 'block' }}>Internal reference name</span>
              </div>

              {/* Attach Image */}
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <PictureOutlined style={{ fontSize: '16px', color: '#6366f1' }} />
                  <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)' }}>Attach Image (MMS)</span>
                </div>
                <div className="image-upload-area" style={{
                  border: '2px dashed var(--paper-300)',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}>
                  <PictureOutlined style={{ fontSize: 24, color: '#9ca3af' }} />
                  <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--ink-200)' }}>Upload</p>
                </div>
                <span style={{ fontSize: '10px', color: '#9ca3af', display: 'block', marginTop: '4px' }}>Max 1MB</span>
              </div>
        </div>

        {/* Row 2: SMS Message - Full Width */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          marginBottom: '24px',
          width: '100%',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>SMS Message</label>
          <p className="form-sublabel" style={{ fontSize: '12px', color: 'var(--ink-200)', marginBottom: '12px' }}>Click to insert personalization tokens:</p>
          
          <div className="token-buttons" style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {personalizationTokens.map(token => (
              <button
                key={token.id}
                className="token-btn"
                onClick={() => insertToken(token.id)}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                {token.icon} {token.label}
              </button>
            ))}
          </div>

          <textarea
            className="form-textarea"
            placeholder="Hi {{first_name}}, we're in your area today! As a valued customer, we wanted to reach out about..."
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value.slice(0, maxChars))}
            rows={4}
            style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <div className="char-count" style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--ink-200)' }}>
              <span>{charCount} / {maxChars} chars</span>
              <span>{smsSegments} segment{smsSegments > 1 ? 's' : ''}</span>
              <span>{charsUntilNextSegment} until next</span>
            </div>
            <div className="opt-out-banner" style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '6px',
              padding: '6px 12px',
              display: 'inline-flex',
              gap: '6px',
              alignItems: 'center'
            }}>
              <CheckOutlined style={{ color: '#16a34a', fontSize: '12px' }} />
              <span style={{ fontSize: '11px', color: '#15803d', fontWeight: 500 }}>Auto Opt-Out Enabled</span>
            </div>
          </div>
        </div>

        {/* Row 3: Message Preview - Full Width */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          marginBottom: '24px',
          width: '100%',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '12px', display: 'block' }}>
            📱 Message Preview
          </label>
          
          {/* Phone Preview */}
          <div style={{
            background: 'var(--ink)',
            borderRadius: '24px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '16px',
              minHeight: '300px'
            }}>
              <div style={{
                background: '#e0f2fe',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '14px',
                lineHeight: 1.5,
                color: '#0c4a6e',
                wordWrap: 'break-word'
              }}>
                {getMessagePreview()}
              </div>
              <div style={{
                marginTop: '12px',
                fontSize: '11px',
                color: '#9ca3af',
                textAlign: 'center'
              }}>
                Reply STOP to unsubscribe
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f9fafb',
            borderRadius: '8px',
            fontSize: '12px',
            maxWidth: '400px',
            margin: '16px auto 0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ color: 'var(--ink-200)' }}>Characters:</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{charCount} / {maxChars}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ color: 'var(--ink-200)' }}>SMS Segments:</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{smsSegments}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-200)' }}>Until Next Segment:</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{charsUntilNextSegment}</span>
            </div>
          </div>
        </div>

        {/* Delivery Settings - 2 Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px', width: '100%' }}>
        
        {/* Cooldown Period Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <ClockCircleOutlined style={{ fontSize: '16px', color: '#6366f1' }} />
            <div>
              <strong style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>Cooldown Period</strong>
              <span style={{ fontSize: '11px', color: 'var(--ink-200)' }}>Minimum time between messages</span>
            </div>
          </div>
          <Select
            style={{ width: '100%', height: 44 }}
            value={cooldownPeriod}
            onChange={setCooldownPeriod}
            options={cooldownOptions}
          />
        </div>

        {/* Quiet Hours Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClockCircleOutlined style={{ fontSize: '16px', color: '#6366f1' }} />
              <div>
                <strong style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>Quiet Hours</strong>
                <span style={{ fontSize: '11px', color: 'var(--ink-200)' }}>Don't send during these hours</span>
              </div>
            </div>
            <Switch 
              checked={quietHoursEnabled} 
              onChange={setQuietHoursEnabled}
            />
          </div>
          {quietHoursEnabled && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: 'var(--ink-200)', display: 'block', marginBottom: '4px' }}>From</label>
                <Select
                  value={quietHoursFrom}
                  onChange={setQuietHoursFrom}
                  style={{ width: '100%', height: 44 }}
                  options={[
                    { value: '8:00 PM', label: '8:00 PM' },
                    { value: '9:00 PM', label: '9:00 PM' },
                    { value: '10:00 PM', label: '10:00 PM' },
                  ]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: 'var(--ink-200)', display: 'block', marginBottom: '4px' }}>To</label>
                <Select
                  value={quietHoursTo}
                  onChange={setQuietHoursTo}
                  style={{ width: '100%', height: 44 }}
                  options={[
                    { value: '7:00 AM', label: '7:00 AM' },
                    { value: '8:00 AM', label: '8:00 AM' },
                    { value: '9:00 AM', label: '9:00 AM' },
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

        {/* TCPA Compliance - Full Width Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          marginBottom: '24px',
          width: '100%',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <SafetyOutlined style={{ fontSize: '18px', color: '#6366f1' }} />
            <strong style={{ fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>TCPA Compliance</strong>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '12px' }}>By using this SMS campaign feature, you confirm that:</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', fontSize: '13px', color: 'var(--ink-100)' }}>
              <CheckOutlined style={{ color: '#16a34a', fontSize: '14px', marginTop: '2px' }} /> 
              <span>All contacts have provided express written consent to receive SMS messages</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', fontSize: '13px', color: 'var(--ink-100)' }}>
              <CheckOutlined style={{ color: '#16a34a', fontSize: '14px', marginTop: '2px' }} /> 
              <span>You will honor all opt-out requests within 24 hours</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', fontSize: '13px', color: 'var(--ink-100)' }}>
              <CheckOutlined style={{ color: '#16a34a', fontSize: '14px', marginTop: '2px' }} /> 
              <span>Messages will include your business name and opt-out instructions</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--ink-100)' }}>
              <CheckOutlined style={{ color: '#16a34a', fontSize: '14px', marginTop: '2px' }} /> 
              <span>You maintain records of consent for each contact</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          {campaignName ? `Campaign: ${campaignName}` : 'Create your SMS campaign'}
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default SMSTemplateDelivery;
